import { useState } from "react";
import {
  Card,
  Row,
  Col,
  Input,
  InputNumber,
  Button,
  Typography,
  Space,
  Empty,
  Tag,
  Upload,
  message,
} from "antd";

import {
  PlusOutlined,
  DeleteOutlined,
  CompassOutlined,
  UploadOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import activityCatalogService from "@/services/activityCatalogService";

const { Title, Text } = Typography;

const LineItemSection = ({
  title,
  buttonText,
  field,
  items = [],
  day,
  updateDay,
  color = "#722ed1",
  onSearchCatalog,
}) => {
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const handleUploadImageForItem = async (index, file) => {
    try {
      setUploadingIndex(index);
      const formData = new FormData();
      formData.append("image", file);
      const res = await activityCatalogService.uploadImage(formData);
      if (res?.data?.imageUrl) {
        const updated = [...items];
        updated[index] = {
          ...updated[index],
          imageUrl: res.data.imageUrl,
          image: res.data.imageUrl,
        };
        updateDay(day.id, { [field]: updated });
        message.success("Image uploaded successfully!");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to upload image");
    } finally {
      setUploadingIndex(null);
    }
  };

  /* =========================================
     ADD ITEM
  ========================================= */

  const addItem = () => {

    const updatedItems = [

      ...items,

      {
        title: "",
        category: "",
        quantity: 1,
        price: 0,
        total: 0,
        notes: "",
      },
    ];

    updateDay(day.id, {

      [field]:
        updatedItems,
    });
  };

  return (

    <>

      {/* =====================================
         HEADER
      ===================================== */}

      <Row
        justify="space-between"
        align="middle"
        style={{
          marginBottom: 18,
        }}
      >

        <Col>

          <Text
            strong
            style={{
              fontSize: 16,
            }}
          >
            {title}
          </Text>

        </Col>

        <Col>
          <Space>
            {onSearchCatalog && (
              <Button
                icon={<CompassOutlined style={{ color: "#1677ff" }} />}
                onClick={onSearchCatalog}
                style={{ borderRadius: 8 }}
              >
                Search Catalog
              </Button>
            )}

            <Button
              type="primary"
              ghost
              icon={<PlusOutlined />}
              onClick={addItem}
            >
              {buttonText}
            </Button>
          </Space>
        </Col>

      </Row>

      {/* =====================================
         ITEMS
      ===================================== */}

      {items.length ? (
        items.map((item, index) => (
          <Card
            key={index}
            size="small"
            style={{
              marginBottom: 14,
              borderRadius: 16,
              background: "#fcfcff",
              border: "1px solid #ececec",
            }}
            bodyStyle={{ padding: 12 }}
          >
            <Row gutter={[12, 8]} align="middle">
              {/* IMAGE THUMBNAIL & UPLOAD / DELETE */}
              <Col xs={24} sm={3}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  {(item.imageUrl || item.image) ? (
                    <div style={{ position: "relative", width: "100%", height: 50, borderRadius: 8, overflow: "hidden", border: "1px solid #cbd5e1" }}>
                      <img
                        src={item.imageUrl || item.image}
                        alt={item.title || "Activity"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <Button
                        type="primary"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        style={{ position: "absolute", top: 2, right: 2, width: 20, height: 20, minWidth: 20, padding: 0, fontSize: 10 }}
                        onClick={() => {
                          const updated = [...items];
                          updated[index] = { ...updated[index], imageUrl: "", image: "" };
                          updateDay(day.id, { [field]: updated });
                        }}
                      />
                    </div>
                  ) : (
                    <Upload
                      customRequest={({ file }) => handleUploadImageForItem(index, file)}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button
                        size="small"
                        icon={<UploadOutlined />}
                        loading={uploadingIndex === index}
                        style={{ fontSize: 11, borderRadius: 6, width: "100%" }}
                      >
                        Photo
                      </Button>
                    </Upload>
                  )}
                </div>
              </Col>

              {/* TITLE */}
              <Col xs={24} sm={6}>
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>
                  Activity Title
                </Text>
                <Input
                  placeholder="e.g. Scuba Diving at Baga Beach"
                  value={item.title}
                  onChange={(e) => {
                    const updated = [...items];
                    updated[index] = { ...updated[index], title: e.target.value };
                    updateDay(day.id, { [field]: updated });
                  }}
                />
              </Col>

              {/* CATEGORY */}
              <Col xs={12} sm={4}>
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>
                  Category
                </Text>
                <Input
                  placeholder="Sightseeing"
                  value={item.category || ""}
                  onChange={(e) => {
                    const updated = [...items];
                    updated[index] = { ...updated[index], category: e.target.value };
                    updateDay(day.id, { [field]: updated });
                  }}
                />
              </Col>

              {/* QUANTITY / PAX */}
              <Col xs={12} sm={3}>
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>
                  Pax (Qty)
                </Text>
                <InputNumber
                  min={1}
                  style={{ width: "100%" }}
                  value={item.quantity || 1}
                  onChange={(value) => {
                    const updated = [...items];
                    const q = value || 1;
                    const p = updated[index].price || 0;
                    updated[index] = {
                      ...updated[index],
                      quantity: q,
                      total: p * q,
                    };
                    updateDay(day.id, { [field]: updated });
                  }}
                />
              </Col>

              {/* PRICE / RATE PER PAX */}
              <Col xs={12} sm={4}>
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 2 }}>
                  Rate / Pax (₹)
                </Text>
                <InputNumber
                  min={0}
                  prefix="₹"
                  style={{ width: "100%" }}
                  value={item.price || 0}
                  onChange={(value) => {
                    const updated = [...items];
                    const p = value || 0;
                    const q = updated[index].quantity || 1;
                    updated[index] = {
                      ...updated[index],
                      price: p,
                      total: p * q,
                    };
                    updateDay(day.id, { [field]: updated });
                  }}
                />
              </Col>

              {/* TOTAL & DELETE */}
              <Col xs={12} sm={4} style={{ display: "flex", alignItems: "center", justifyBetween: "space-between", gap: 8 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                    Total
                  </Text>
                  <Title level={5} style={{ margin: 0, color, fontSize: 15, fontWeight: 700 }}>
                    ₹{Number(item.total || 0).toLocaleString()}
                  </Title>
                </div>
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    const updated = [...items];
                    updated.splice(index, 1);
                    updateDay(day.id, { [field]: updated });
                  }}
                />
              </Col>
            </Row>

            {/* INCLUSIONS & DURATION BADGES */}
            {(item.duration || (item.inclusions && item.inclusions.length > 0)) && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed #f1f5f9", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {item.duration && (
                  <Tag color="cyan" style={{ fontSize: 10, borderRadius: 4 }}>
                    ⏱️ {item.duration}
                  </Tag>
                )}
                {item.inclusions && Array.isArray(item.inclusions) && item.inclusions.map((inc, i) => (
                  <Tag key={i} color="green" style={{ fontSize: 10, borderRadius: 4 }}>
                    ✓ {inc}
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        ))
      ) : (

        <Card
          size="small"

          style={{

            borderRadius: 14,

            border:
              "1px dashed #d1d5db",

            background:
              "#fafafa",
          }}
        >

          <Empty
            image={
              Empty.PRESENTED_IMAGE_SIMPLE
            }

            description={`No ${title} added`}
          />

        </Card>

      )}

    </>
  );
};

export default LineItemSection;
